export type ElectionBoardRow = {
  id: string
  rank: number
  label: string
  subtitle?: string
  badgeLabel?: string
  barColor: string
  imageUrl?: string | null
  stakePercent: number
  totalStake: number
  totalBets: number
  isFavorite?: boolean
}

export type PopularityLike = {
  totalStake: number
  totalBets: number
  updatedAt?: string
  selections: Array<{
    selectionId: string
    label: string
    subtitle?: string
    badgeLabel?: string
    barColor: string
    imageUrl?: string | null
    stakePercent: number
    totalStake: number
    totalBets?: number
    isFavorite?: boolean
  }>
}

export const ELECTION_DISPLAY_TOP_N = 5

/** น้ำหนัก demo ให้ top 5 ดูสมจริง (ชัชชาติ, สุชัชวีร์, ฯลฯ) */
const DEMO_CANDIDATE_BOOST: Record<number, number> = {
  8: 3.4,
  4: 2.15,
  1: 1.35,
  2: 1.05,
  13: 0.95,
  6: 0.75,
  21: 0.65,
  10: 0.55,
}

function selectionNo(selectionCode?: string, fallback = 99): number {
  const match = /^NO_(\d+)$/i.exec(String(selectionCode || '').trim())
  return match ? parseInt(match[1], 10) : fallback
}

export function mapPopularityToElectionRows(
  popularity: PopularityLike,
  maxVisible = ELECTION_DISPLAY_TOP_N,
): ElectionBoardRow[] {
  const sorted = [...popularity.selections].sort((a, b) => {
    if (b.totalStake !== a.totalStake) return b.totalStake - a.totalStake
    return (b.totalBets ?? 0) - (a.totalBets ?? 0)
  })

  return sorted.slice(0, maxVisible).map((sel, index) => ({
    id: String(sel.selectionId),
    rank: index + 1,
    label: sel.label,
    subtitle: sel.subtitle,
    badgeLabel: sel.badgeLabel,
    barColor: sel.barColor || '#3B82F6',
    imageUrl: sel.imageUrl,
    stakePercent: sel.stakePercent,
    totalStake: sel.totalStake,
    totalBets: sel.totalBets ?? 0,
    isFavorite: sel.isFavorite,
  }))
}

export function buildDemoElectionRows(
  selections: Array<{
    key: string
    label: string
    subtitle?: string
    barColor?: string | null
    selectionCode?: string
    imageUrl?: string | null
  }>,
  seed = 1,
  maxVisible = ELECTION_DISPLAY_TOP_N,
): ElectionBoardRow[] {
  const scored = selections.map((sel, i) => {
    const no = selectionNo(sel.selectionCode, i + 1)
    const boost = DEMO_CANDIDATE_BOOST[no] ?? 0.25 + 1 / (no + 3)
    const wave = Math.sin((seed + i) * 0.55) * 0.07
    return { sel, weight: Math.max(0.02, boost + wave) }
  })

  scored.sort((a, b) => b.weight - a.weight)
  const top = scored.slice(0, maxVisible)
  const sum = top.reduce((acc, row) => acc + row.weight, 0)
  const totalStake = 1_245_680 + (seed % 11) * 18_420

  return top.map(({ sel, weight }, index) => {
    const ratio = weight / sum
    const stakePercent = Math.round(ratio * 1000) / 10
    const totalStakeRow = Math.round(totalStake * ratio)
    const totalBets = Math.max(12, Math.round(totalStakeRow / (95 + index * 18)))
    return {
      id: sel.key,
      rank: index + 1,
      label: sel.label || `ตัวเลือก ${index + 1}`,
      subtitle: sel.subtitle || sel.selectionCode,
      barColor: sel.barColor || '#3B82F6',
      imageUrl: sel.imageUrl,
      stakePercent,
      totalStake: totalStakeRow,
      totalBets,
      isFavorite: index === 0,
    }
  })
}

export function demoTotalsFromRows(rows: ElectionBoardRow[]) {
  return {
    totalStake: rows.reduce((sum, row) => sum + row.totalStake, 0),
    totalBets: rows.reduce((sum, row) => sum + row.totalBets, 0),
  }
}
